import React, { useState } from 'react';
import { Modal, Button, Typography, Spin, Space } from 'antd';
import { importAllDataFromApi } from '../api';

// Helper function to format field names
const formatFieldName = (fieldName) => {
  // Remove '_id' suffix if present
  let cleanName = fieldName.endsWith('_id') ? fieldName.slice(0, -3) : fieldName;
  
  return cleanName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper component to display updated fields in a table format
const UpdatedFieldsTable = ({ updatedFields }) => {
  if (!updatedFields || Object.keys(updatedFields).length === 0) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <table style={{ 
        width: '100%', 
        fontSize: '12px', 
        borderCollapse: 'collapse',
        backgroundColor: '#fafafa',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid #e0e0e0' }}>
            <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', color: '#595959' }}>Field</th>
            <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', color: '#595959' }}>Previous</th>
            <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: '600', color: '#595959' }}>Updated</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(updatedFields).map(([field, [oldValue, newValue]], index) => (
            <tr key={field} style={{ 
              borderBottom: index < Object.keys(updatedFields).length - 1 ? '1px solid #e8e8e8' : 'none'
            }}>
              <td style={{ padding: '6px 8px', color: '#262626', fontWeight: '500' }}>
                {formatFieldName(field)}
              </td>
              <td style={{ padding: '6px 8px', color: '#8c8c8c', fontStyle: oldValue ? 'normal' : 'italic' }}>
                {oldValue || '(empty)'}
              </td>
              <td style={{ padding: '6px 8px', color: '#262626' }}>
                {newValue || '(empty)'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


export const renderClientsSummary = (clientSyncSummary) => {
  const ClientCard = ({ client, isNew = false }) => (
    <div
      style={{
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        borderLeft: isNew ? '4px solid #52c41a' : '4px solid #1890ff',
      }}
    >
      <Typography.Text strong style={{ fontSize: '14px' }}>
        {client.client_name}
      </Typography.Text>

      {/* Updated fields */}
      <UpdatedFieldsTable updatedFields={client.updated_fields} />
    </div>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Added Clients */}
      {clientSyncSummary?.added?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#52c41a' }}>
            ✓ New Clients Added ({clientSyncSummary.added.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {clientSyncSummary.added.map((client, index) => (
              <ClientCard key={index} client={client} isNew={true} />
            ))}
          </div>
        </div>
      )}

      {/* Updated Clients */}
      {clientSyncSummary?.updated?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#1890ff' }}>
            ↻ Clients Updated ({clientSyncSummary.updated.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {clientSyncSummary.updated.map((client, index) => (
              <ClientCard key={index} client={client} />
            ))}
          </div>
        </div>
      )}

      {/* Skipped Clients */}
      {clientSyncSummary?.skipped?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#ff4d4f' }}>
            ✕ Clients Skipped ({clientSyncSummary.skipped.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {clientSyncSummary.skipped.map((client, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#fff1f0',
                  borderRadius: '6px',
                  borderLeft: '4px solid #ff4d4f',
                }}
              >
                <Typography.Text strong style={{ fontSize: '14px' }}>
                  Client ID: {client.client_id}
                </Typography.Text>
                <Typography.Text style={{ fontSize: '13px', display: 'block', marginTop: '4px', color: '#595959' }}>
                  Reason: {client.reason}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Removed Clients */}
      {clientSyncSummary?.removed?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#8c8c8c' }}>
            ⊖ Clients Removed ({clientSyncSummary.removed.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {clientSyncSummary.removed.map((client, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  borderLeft: '4px solid #d9d9d9',
                }}
              >
                <Typography.Text style={{ fontSize: '14px', color: '#8c8c8c' }}>
                  {client.client_name}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No changes */}
      {(!clientSyncSummary?.added || clientSyncSummary.added.length === 0) &&
        (!clientSyncSummary?.updated || clientSyncSummary.updated.length === 0) &&
        (!clientSyncSummary?.skipped || clientSyncSummary.skipped.length === 0) &&
        (!clientSyncSummary?.removed || clientSyncSummary.removed.length === 0) && (
          <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
            No changes detected
          </Typography.Text>
        )}
    </Space>
  );
};

export const renderSitesSummary = (siteSyncSummary) => {
  const SiteCard = ({ site, isNew = false }) => (
    <div
      style={{
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        borderLeft: isNew ? '4px solid #52c41a' : '4px solid #1890ff',
      }}
    >
      <Typography.Text strong style={{ fontSize: '14px' }}>
        {site.site_name}
      </Typography.Text>
      <Typography.Text style={{ fontSize: '13px', display: 'block', color: '#595959', marginTop: '2px' }}>
        Client: {site.client_name}
      </Typography.Text>

      {/* Updated fields */}
      <UpdatedFieldsTable updatedFields={site.updated_fields} />
    </div>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Added Sites */}
      {siteSyncSummary?.added?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#52c41a' }}>
            ✓ New Sites Added ({siteSyncSummary.added.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {siteSyncSummary.added.map((site, index) => (
              <SiteCard key={index} site={site} isNew={true} />
            ))}
          </div>
        </div>
      )}

      {/* Updated Sites */}
      {siteSyncSummary?.updated?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#1890ff' }}>
            ↻ Sites Updated ({siteSyncSummary.updated.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {siteSyncSummary.updated.map((site, index) => (
              <SiteCard key={index} site={site} />
            ))}
          </div>
        </div>
      )}

      {/* Skipped Sites */}
      {siteSyncSummary?.skipped?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#ff4d4f' }}>
            ✕ Sites Skipped ({siteSyncSummary.skipped.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {siteSyncSummary.skipped.map((site, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#fff1f0',
                  borderRadius: '6px',
                  borderLeft: '4px solid #ff4d4f',
                }}
              >
                <Typography.Text strong style={{ fontSize: '14px' }}>
                  Site ID: {site.site_id}
                </Typography.Text>
                <Typography.Text style={{ fontSize: '13px', display: 'block', marginTop: '4px', color: '#595959' }}>
                  Reason: {site.reason}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Removed Sites */}
      {siteSyncSummary?.removed?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#8c8c8c' }}>
            ⊖ Sites Removed ({siteSyncSummary.removed.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {siteSyncSummary.removed.map((site, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  borderLeft: '4px solid #d9d9d9',
                }}
              >
                <Typography.Text style={{ fontSize: '14px', color: '#8c8c8c' }}>
                  {site.site_name}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No changes */}
      {(!siteSyncSummary?.added || siteSyncSummary.added.length === 0) &&
        (!siteSyncSummary?.updated || siteSyncSummary.updated.length === 0) &&
        (!siteSyncSummary?.skipped || siteSyncSummary.skipped.length === 0) &&
        (!siteSyncSummary?.removed || siteSyncSummary.removed.length === 0) && (
          <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
            No changes detected
          </Typography.Text>
        )}
    </Space>
  );
};

export const renderSubcontractorsSummary = (subcontractorSyncSummary) => {
  const SubcontractorCard = ({ subcontractor, isNew = false }) => (
    <div
      style={{
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        borderLeft: isNew ? '4px solid #52c41a' : '4px solid #1890ff',
      }}
    >
      <Typography.Text strong style={{ fontSize: '14px' }}>
        {subcontractor.subcontractor_name}
      </Typography.Text>

      {/* Updated fields */}
      <UpdatedFieldsTable updatedFields={subcontractor.updated_fields} />
    </div>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Added Subcontractors */}
      {subcontractorSyncSummary?.added?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#52c41a' }}>
            ✓ New Subcontractors Added ({subcontractorSyncSummary.added.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {subcontractorSyncSummary.added.map((subcontractor, index) => (
              <SubcontractorCard key={index} subcontractor={subcontractor} isNew={true} />
            ))}
          </div>
        </div>
      )}

      {/* Updated Subcontractors */}
      {subcontractorSyncSummary?.updated?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#1890ff' }}>
            ↻ Subcontractors Updated ({subcontractorSyncSummary.updated.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {subcontractorSyncSummary.updated.map((subcontractor, index) => (
              <SubcontractorCard key={index} subcontractor={subcontractor} />
            ))}
          </div>
        </div>
      )}

      {/* Skipped Subcontractors */}
      {subcontractorSyncSummary?.skipped?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#ff4d4f' }}>
            ✕ Subcontractors Skipped ({subcontractorSyncSummary.skipped.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {subcontractorSyncSummary.skipped.map((subcontractor, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#fff1f0',
                  borderRadius: '6px',
                  borderLeft: '4px solid #ff4d4f',
                }}
              >
                <Typography.Text strong style={{ fontSize: '14px' }}>
                  Subcontractor ID: {subcontractor.subcontractor_id}
                </Typography.Text>
                <Typography.Text style={{ fontSize: '13px', display: 'block', marginTop: '4px', color: '#595959' }}>
                  Reason: {subcontractor.reason}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Removed Subcontractors */}
      {subcontractorSyncSummary?.removed?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#8c8c8c' }}>
            ⊖ Subcontractors Removed ({subcontractorSyncSummary.removed.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {subcontractorSyncSummary.removed.map((subcontractor, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  borderLeft: '4px solid #d9d9d9',
                }}
              >
                <Typography.Text style={{ fontSize: '14px', color: '#8c8c8c' }}>
                  {subcontractor.subcontractor_name}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No changes */}
      {(!subcontractorSyncSummary?.added || subcontractorSyncSummary.added.length === 0) &&
        (!subcontractorSyncSummary?.updated || subcontractorSyncSummary.updated.length === 0) &&
        (!subcontractorSyncSummary?.skipped || subcontractorSyncSummary.skipped.length === 0) &&
        (!subcontractorSyncSummary?.removed || subcontractorSyncSummary.removed.length === 0) && (
          <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
            No changes detected
          </Typography.Text>
        )}
    </Space>
  );
};

export const renderRoutesSummary = (routeSyncSummary) => {
  const hasWarnings = (route) => {
    return route.warnings && route.warnings.length > 0;
  };

  const hasSkipped = (route) => {
    return route.skipped_sites_or_subcontractors && route.skipped_sites_or_subcontractors.length > 0;
  };

  const RouteCard = ({ route, isNew = false }) => (
    <div
      style={{
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: hasWarnings(route) || hasSkipped(route) ? '#fffbe6' : '#f5f5f5',
        borderRadius: '6px',
        borderLeft: hasWarnings(route) || hasSkipped(route) ? '4px solid #faad14' : '4px solid #52c41a',
      }}
    >
      <Typography.Text strong style={{ fontSize: '14px' }}>
        {route.route_name}
      </Typography.Text>

      {/* Warnings - Most prominent */}
      {route.warnings?.map((warning, idx) => (
        <div
          key={`warning-${idx}`}
          style={{
            marginTop: '8px',
            padding: '8px',
            backgroundColor: '#fff7e6',
            borderRadius: '4px',
            borderLeft: '3px solid #fa8c16',
          }}
        >
          <Typography.Text style={{ fontSize: '13px', color: '#ad6800' }}>
            ⚠️ {warning}
          </Typography.Text>
        </div>
      ))}

      {/* Skipped items */}
      {route.skipped_sites_or_subcontractors?.map((skipped, idx) => (
        <div
          key={`skipped-${idx}`}
          style={{
            marginTop: '8px',
            padding: '8px',
            backgroundColor: '#fff1f0',
            borderRadius: '4px',
            borderLeft: '3px solid #ff7875',
          }}
        >
          <Typography.Text style={{ fontSize: '13px', color: '#a8071a' }}>
            ⚠️ {skipped}
          </Typography.Text>
        </div>
      ))}

      {/* Updated fields */}
      <UpdatedFieldsTable updatedFields={route.updated_fields} />

      {/* Added sites */}
      {route.added_sites?.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Added Sites:
          </Typography.Text>
          <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#595959' }}>
            {route.added_sites.map((site, idx) => (
              <li key={idx}>{site}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Removed sites */}
      {route.removed_sites?.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Removed Sites:
          </Typography.Text>
          <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#595959' }}>
            {route.removed_sites.map((site, idx) => (
              <li key={idx}>{site}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Added subcontractors */}
      {route.added_subcontractors?.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Added Subcontractors:
          </Typography.Text>
          <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#595959' }}>
            {route.added_subcontractors.map((subcontractor, idx) => (
              <li key={idx}>{subcontractor}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Added Routes */}
      {routeSyncSummary?.added?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#52c41a' }}>
            ✓ New Routes Added ({routeSyncSummary.added.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {routeSyncSummary.added.map((route, index) => (
              <RouteCard key={index} route={route} isNew={true} />
            ))}
          </div>
        </div>
      )}

      {/* Updated Routes */}
      {routeSyncSummary?.updated?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#1890ff' }}>
            ↻ Routes Updated ({routeSyncSummary.updated.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {routeSyncSummary.updated.map((route, index) => (
              <RouteCard key={index} route={route} />
            ))}
          </div>
        </div>
      )}

      {/* Skipped Routes */}
      {routeSyncSummary?.skipped?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#ff4d4f' }}>
            ✕ Routes Skipped ({routeSyncSummary.skipped.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {routeSyncSummary.skipped.map((route, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#fff1f0',
                  borderRadius: '6px',
                  borderLeft: '4px solid #ff4d4f',
                }}
              >
                <Typography.Text strong style={{ fontSize: '14px' }}>
                  {route.route_name || `Route ID: ${route.route_id}`}
                </Typography.Text>
                <Typography.Text style={{ fontSize: '13px', display: 'block', marginTop: '4px', color: '#595959' }}>
                  Reason: {route.reason}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Removed Routes */}
      {routeSyncSummary?.removed?.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: '15px', color: '#8c8c8c' }}>
            ⊖ Routes Removed ({routeSyncSummary.removed.length})
          </Typography.Text>
          <div style={{ marginTop: '12px' }}>
            {routeSyncSummary.removed.map((route, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  borderLeft: '4px solid #d9d9d9',
                }}
              >
                <Typography.Text style={{ fontSize: '14px', color: '#8c8c8c' }}>
                  {route.route_name}
                </Typography.Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No changes */}
      {(!routeSyncSummary?.added || routeSyncSummary.added.length === 0) &&
        (!routeSyncSummary?.updated || routeSyncSummary.updated.length === 0) &&
        (!routeSyncSummary?.skipped || routeSyncSummary.skipped.length === 0) &&
        (!routeSyncSummary?.removed || routeSyncSummary.removed.length === 0) && (
          <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
            No changes detected
          </Typography.Text>
        )}
    </Space>
  );
};

const YetiSync = ({ open, onClose, isSyncing }) => {
  const [isImporting, setIsImporting] = useState(false);
  const [currentImportStep, setCurrentImportStep] = useState('');
  const [importSummary, setImportSummary] = useState(null);

  const handleImport = async () => {
    setIsImporting(true);
    setImportSummary(null);

    try {
      isSyncing(true);
      const summary = await importAllDataFromApi((step) => setCurrentImportStep(step));
      console.log('Import Summary:', summary);
      setImportSummary(summary);
    } catch (error) {
      console.error('Error importing data:', error);
    } finally {
      isSyncing(false);
    }

    setIsImporting(false);
    setCurrentImportStep('');
  };

  const handleClose = () => {
    setImportSummary(null);
    onClose();
  };

  return (
    <Modal
      title={importSummary ? "Yeti Sync Summary" : "Sync Yeti Data"}
      open={open}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key="close" onClick={handleClose} disabled={isImporting}>
          Close
        </Button>,
        !importSummary && (
          <Button key="start" type="primary" onClick={handleImport} disabled={isImporting}>
            Start Sync
          </Button>
        ),
      ]}
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto',
          paddingTop: importSummary ? '16px' : '24px',
        },
      }}
    >
      {isImporting ? (
        <Space direction="vertical" size="middle" style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <Typography.Text style={{ fontSize: '16px' }}>
            Syncing {currentImportStep}...
          </Typography.Text>
        </Space>
      ) : importSummary ? (
        <div>
          <Typography.Title level={5} style={{ marginTop: 0, marginBottom: '16px', color: '#262626' }}>
            Clients
          </Typography.Title>
          {renderClientsSummary(importSummary.clients)}
          
          <Typography.Title level={5} style={{ marginTop: '32px', marginBottom: '16px', color: '#262626' }}>
            Sites
          </Typography.Title>
          {renderSitesSummary(importSummary.sites)}
          
          <Typography.Title level={5} style={{ marginTop: '32px', marginBottom: '16px', color: '#262626' }}>
            Subcontractors
          </Typography.Title>
          {renderSubcontractorsSummary(importSummary.subcontractors)}
          
          <Typography.Title level={5} style={{ marginTop: '32px', marginBottom: '16px', color: '#262626' }}>
            Routes
          </Typography.Title>
          {renderRoutesSummary(importSummary.routes)}
        </div>
      ) : (
        <div style={{ padding: '20px 0' }}>
          <Typography.Text style={{ fontSize: '14px', color: '#595959' }}>
            This will sync all data from Yeti, including clients, sites, subcontractors, and routes. New records will be added and existing records will be updated with the latest information.
          </Typography.Text>
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', borderLeft: '4px solid #1890ff' }}>
            <Typography.Text style={{ fontSize: '13px', color: '#0050b3' }}>
              💡 This process will take several minutes, once started please do not refresh the page or navigate away until it is complete.
            </Typography.Text>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default YetiSync;